import { Type, type Static } from "typebox";
import { OrganizationStatusSchema, OwnershipHealthSchema } from "./organizations.ts";
import { OrganizationRoleSchema } from "./identity.ts";

const InternalIdSchema = Type.String({ format: "uuid" });
const NameSchema = Type.String({ minLength: 1, maxLength: 200 });
const EmailSchema = Type.String({ format: "email", maxLength: 320 });
const NullableEmailSchema = Type.Union([
  EmailSchema,
  Type.Null(),
]);
const WorkOSUserIdSchema = Type.String({
  minLength: 6,
  maxLength: 255,
  pattern: "^user_[A-Za-z0-9_-]+$",
});

export const PlatformAdminAssignmentV1Schema = Type.Object({
  version: Type.Literal(1),
  id: InternalIdSchema,
  userId: InternalIdSchema,
  displayName: NameSchema,
  email: NullableEmailSchema,
  role: Type.Literal("f5_admin"),
  grantedAt: Type.String({ format: "date-time" }),
  grantedByUserId: Type.Union([InternalIdSchema, Type.Null()]),
  canRevoke: Type.Boolean(),
}, { additionalProperties: false });
export type PlatformAdminAssignmentV1 = Static<typeof PlatformAdminAssignmentV1Schema>;

/** The one narrow inbound exception that accepts a WorkOS identifier. */
export const GrantPlatformAdminRequestV1Schema = Type.Object({
  version: Type.Literal(1),
  workosUserId: WorkOSUserIdSchema,
}, { additionalProperties: false });
export type GrantPlatformAdminRequestV1 = Static<typeof GrantPlatformAdminRequestV1Schema>;

export const PlatformAdminAssignmentListResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  assignments: Type.Array(PlatformAdminAssignmentV1Schema, { maxItems: 100 }),
}, { additionalProperties: false });
export type PlatformAdminAssignmentListResponseV1 = Static<typeof PlatformAdminAssignmentListResponseV1Schema>;

// Keep the short names available for adapters that describe these resources
// without the surrounding assignment collection.
export const PlatformAdminGrantRequestV1Schema = GrantPlatformAdminRequestV1Schema;
export type PlatformAdminGrantRequestV1 = GrantPlatformAdminRequestV1;
export const PlatformAdminListResponseV1Schema = PlatformAdminAssignmentListResponseV1Schema;
export type PlatformAdminListResponseV1 = PlatformAdminAssignmentListResponseV1;

export const AdminOrganizationSummaryV1Schema = Type.Object({
  version: Type.Literal(1),
  id: InternalIdSchema,
  name: NameSchema,
  status: OrganizationStatusSchema,
  ownershipHealth: OwnershipHealthSchema,
  billingAccessStatus: Type.Union([
    Type.Literal("entitled"),
    Type.Literal("grace"),
    Type.Literal("blocked"),
    Type.Null(),
  ]),
  createdAt: Type.String({ format: "date-time" }),
}, { additionalProperties: false });
export type AdminOrganizationSummaryV1 = Static<typeof AdminOrganizationSummaryV1Schema>;

/**
 * A minimal F5-only selection surface for repairing unhealthy ownership. It
 * intentionally omits user and provider identifiers; `id` is the internal
 * membership ID accepted by the recovery command.
 */
export const AdminOrganizationRecoveryCandidateV1Schema = Type.Object({
  version: Type.Literal(1),
  id: InternalIdSchema,
  displayName: NameSchema,
  email: NullableEmailSchema,
  role: OrganizationRoleSchema,
}, { additionalProperties: false });
export type AdminOrganizationRecoveryCandidateV1 = Static<typeof AdminOrganizationRecoveryCandidateV1Schema>;

const RecoveryOwnershipHealthSchema = Type.Union([
  Type.Literal("missing"),
  Type.Literal("multiple"),
]);
const NullableOpaqueCursorSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 512 }),
  Type.Null(),
]);

export const AdminOrganizationRecoveryCandidatesResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  ownershipHealth: RecoveryOwnershipHealthSchema,
  candidates: Type.Array(AdminOrganizationRecoveryCandidateV1Schema, { maxItems: 100 }),
  nextCursor: NullableOpaqueCursorSchema,
}, { additionalProperties: false });
export type AdminOrganizationRecoveryCandidatesResponseV1 = Static<
  typeof AdminOrganizationRecoveryCandidatesResponseV1Schema
>;

const AdminSearchSchema = Type.String({ minLength: 1, maxLength: 200 });

/**
 * This is deliberately a small staff-only search surface. It does not accept
 * provider identifiers, owner identifiers, or an arbitrary page size.
 */
export const AdminOrganizationListQueryV1Schema = Type.Object({
  search: Type.Optional(AdminSearchSchema),
  status: Type.Optional(OrganizationStatusSchema),
  after: Type.Optional(InternalIdSchema),
}, { additionalProperties: false });
export type AdminOrganizationListQueryV1 = Static<typeof AdminOrganizationListQueryV1Schema>;

export const AdminOrganizationListResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  items: Type.Array(AdminOrganizationSummaryV1Schema, { maxItems: 50 }),
  nextAfter: Type.Union([InternalIdSchema, Type.Null()]),
}, { additionalProperties: false });
export type AdminOrganizationListResponseV1 = Static<typeof AdminOrganizationListResponseV1Schema>;

export const AdminOrganizationStatusResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  status: Type.Union([Type.Literal("active"), Type.Literal("suspended")]),
}, { additionalProperties: false });
export type AdminOrganizationStatusResponseV1 = Static<typeof AdminOrganizationStatusResponseV1Schema>;

/**
 * F5-only: platform accept link for a pending WorkOS invitation (owner preferred).
 * The token is embedded in `inviteLink` only; clients must not log this payload.
 */
export const AdminPendingInviteLinkResponseV1Schema = Type.Object({
  version: Type.Literal(1),
  organizationId: InternalIdSchema,
  email: EmailSchema,
  role: Type.Union([
    Type.Literal("owner"),
    Type.Literal("admin"),
    Type.Literal("member"),
  ]),
  state: Type.Literal("pending"),
  expiresAt: Type.String({ format: "date-time" }),
  /** Same-origin Platform login URL with invitation_token query param. */
  inviteLink: Type.String({ minLength: 16, maxLength: 2048, format: "uri" }),
}, { additionalProperties: false });
export type AdminPendingInviteLinkResponseV1 = Static<typeof AdminPendingInviteLinkResponseV1Schema>;
